import React, { useEffect, useRef, useState } from "react";

import CloseIcon from "@mui/icons-material/Close";
import { Box, Button, Dialog, IconButton, LinearProgress, Stack, TextField, Typography } from "@mui/material";

interface Props {
  open: boolean;
  loading?: boolean;
  onClose: () => void;
  onRerun: (instructions: string) => void;
}

const SLOW_THRESHOLD = 30000;

export const RerunExtractorDialog: React.FC<Props> = ({ open, loading, onClose, onRerun }) => {
  const [instructions, setInstructions] = useState("");
  const [showSlowMessage, setShowSlowMessage] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (loading) {
      timerRef.current = setTimeout(() => setShowSlowMessage(true), SLOW_THRESHOLD);
    } else {
      setShowSlowMessage(false);
      clearTimeout(timerRef.current);
    }
    return () => clearTimeout(timerRef.current);
  }, [loading]);

  const handleRerun = () => {
    onRerun(instructions);
  };

  const handleClose = () => {
    setInstructions("");
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : handleClose}
      slotProps={{
        paper: {
          sx: {
            borderRadius: "8px",
            p: "32px",
            gap: "20px",
            maxWidth: 560,
            width: "100%",
            boxShadow:
              "0px 2px 4px -1px rgba(0,0,0,0.2), 0px 4px 5px 0px rgba(0,0,0,0.14), 0px 1px 10px 0px rgba(0,0,0,0.12)"
          }
        }
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ borderBottom: "1px solid #dbe8f5", pb: "8px" }}
      >
        <Typography sx={{ fontSize: "18px", fontWeight: 400, lineHeight: "24px", color: "#071f35" }}>
          Rerun AI Extractor
        </Typography>
        {!loading && (
          <IconButton onClick={handleClose} sx={{ p: "4px" }}>
            <CloseIcon sx={{ fontSize: 16, color: "#071f35" }} />
          </IconButton>
        )}
      </Stack>

      {loading ? (
        <Box sx={{ bgcolor: "#f6f8fc", py: "10px", px: "10px" }}>
          <Stack direction="row" alignItems="center" gap="10px">
            <Typography
              sx={{ fontSize: "15px", fontWeight: 400, lineHeight: "20px", color: "#071f35", width: 68, flexShrink: 0 }}
            >
              Extraction
            </Typography>
            <Box sx={{ flex: 1, py: "10px" }}>
              <LinearProgress
                sx={{
                  height: 6,
                  borderRadius: 0,
                  bgcolor: "#dbe8f5",
                  "& .MuiLinearProgress-bar": { bgcolor: "#071f35" }
                }}
              />
            </Box>
            <Box sx={{ width: 24, flexShrink: 0 }} />
          </Stack>
          {showSlowMessage && (
            <Typography sx={{ fontSize: "13px", fontWeight: 400, color: "#496682", mt: "8px" }}>
              Extraction is taking longer than usual. Please wait.
            </Typography>
          )}
        </Box>
      ) : (
        <Stack gap="10px">
          <Typography sx={{ fontSize: "16px", fontWeight: 400, lineHeight: "24px", color: "#496682" }}>
            Add instructions to help the AI find specific values. Currently supports capital calls, capital
            distributions, and capital account statements.
          </Typography>
          <TextField
            multiline
            rows={3}
            placeholder="E.g. 'Focus on the distribution line on page 3' or 'The capital call amount is net of fees'"
            value={instructions}
            onChange={(e) => setInstructions(e.target.value)}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                fontSize: "16px",
                "& fieldset": { borderColor: "#7b92a9" }
              }
            }}
          />
        </Stack>
      )}

      <Stack direction="row" justifyContent="flex-end" gap="8px">
        {loading ? (
          <Button
            disabled
            sx={{
              borderRadius: "32px",
              border: "1px solid #8d98a2",
              bgcolor: "white",
              color: "#8d98a2",
              textTransform: "none",
              px: "16px",
              py: "4px",
              fontSize: "16px",
              fontWeight: 400,
              lineHeight: "24px",
              "&:disabled": { border: "1px solid #8d98a2", color: "#8d98a2" }
            }}
          >
            Close
          </Button>
        ) : (
          <>
            <Button
              onClick={handleClose}
              sx={{
                borderRadius: "32px",
                border: "1px solid #7b92a9",
                bgcolor: "white",
                color: "#071f35",
                textTransform: "none",
                px: "16px",
                py: "4px",
                fontSize: "16px",
                fontWeight: 400,
                lineHeight: "24px",
                "&:hover": { bgcolor: "#f6f8fc" }
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleRerun}
              sx={{
                borderRadius: "32px",
                bgcolor: "#071f35",
                color: "white",
                textTransform: "none",
                px: "16px",
                py: "4px",
                fontSize: "16px",
                fontWeight: 400,
                lineHeight: "24px",
                "&:hover": { bgcolor: "#0a2a45" }
              }}
            >
              ReRun
            </Button>
          </>
        )}
      </Stack>
    </Dialog>
  );
};
